/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Box, Button, Divider, Stack, Typography, styled } from "@mui/material";
import { FC } from "react";
import iconClose from "./assets/icon_close.svg";
import { useTranslation } from "react-i18next";
import { DisplayItem } from "components/Common/DisplayItem";
import { formatDate } from "utils/DateTime";
import { Const } from "const";
import { useUserContext } from "context/UserContext";
import { TaskRegisterPanelFooter } from "components/Common/TaskRegisterPanelFooter";

export interface ClosureInfo {
  /** Position */
  position: google.maps.LatLngLiteral;
  /** Is new closure */
  isNew: boolean;
  /** Input user */
  inputUser?: string;
  /** Input date/time */
  inputDate?: Date;
}

interface Props {
  /** Route closure data */
  closure?: ClosureInfo;
  /** Can edit traffic or not */
  editable?: boolean;
  /** Can save edit or not */
  saveEnable?: boolean;
  /** Action on cancel button clicked */
  onCancelClick?: () => void;
  /** Action on save button clicked */
  onSaveClick?: () => void;
  /** Action on delete button clicked */
  onDeleteClick?: () => void;
}

/**
 * Component of route closure detail
 * @param {Props} param0 Properties
 * @return {FC} Component
 */
export const DetailPanel: FC<Props> = ({
  closure,
  editable,
  saveEnable,
  onCancelClick,
  onSaveClick,
  onDeleteClick,
}: Props) => {
  const { jobsite } = useUserContext();
  const { t } = useTranslation();
  const positionValue = () => {
    let valueDisplay = "";
    if (closure) {
      valueDisplay = `${closure.position.lat.toFixed(
        5
      )}, ${closure.position.lng.toFixed(5)}`;
    }
    return valueDisplay;
  };
  const inputDateValue = () => {
    let valueDisplay = "";
    if (closure?.inputDate) {
      valueDisplay = formatDate(
        closure.inputDate,
        Const.dateTimeFormat,
        jobsite?.timezone
      );
    }
    return valueDisplay;
  };
  return (
    <Container>
      <HeaderArea>
        {closure && (
          <>
            <Stack direction={"row"} alignItems={"center"}>
              <img src={iconClose} alt="stop" />
              <Typography variant="h5">{t("AWVUI-MSG62")}</Typography>
            </Stack>
            {editable && (
              <>
                <Spacer />
                <HeaderButtonArea>
                  <Button
                    onClick={onDeleteClick}
                    variant="outlined"
                    color="error"
                  >
                    {t("AWVUI-MSG13")}
                  </Button>
                </HeaderButtonArea>
              </>
            )}
          </>
        )}
      </HeaderArea>
      <ContentsArea>
        {closure && (
          <>
            <DisplayItem
              title={t("AWVUI-MSG63")}
              value={positionValue()}
              boxProps={{
                marginBottom: 5,
              }}
            />
            <Divider />
            {!closure?.isNew && (
              <>
                <DisplayItem
                  title={t("AWVUI-MSG159")}
                  value={closure.inputUser ?? ""}
                  boxProps={{
                    marginBottom: 5,
                  }}
                />
                <DisplayItem
                  title={t("AWVUI-MSG160")}
                  value={inputDateValue()}
                  boxProps={{
                    marginBottom: 5,
                  }}
                />
              </>
            )}
          </>
        )}
      </ContentsArea>
      {editable && (
        <TaskRegisterPanelFooter
          leftLabel={t("AWVUI-MSG12")}
          leftHandle={() => onCancelClick?.()}
          rightLabel={t("AWVUI-MSG73")}
          rightHandle={() => onSaveClick?.()}
          disabled={saveEnable !== undefined ? !saveEnable : true}
        />
      )}
    </Container>
  );
};

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
}));

const HeaderArea = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1, 2),
  height: 55,
  width: "100%",
  backgroundColor: Const.sidePanelHeaderColor,
}));

const Spacer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexGrow: 1,
}));

const HeaderButtonArea = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
}));

const ContentsArea = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1, 2),
  flexGrow: 1,
  backgroundColor: Const.sidePanelBodyColor,
  flexDirection: "column",
}));
